function normalizePreviewText(value) {
  return typeof value === "string" ? value.trim() : "";
}

export function collectOptimizationPreviewRetainedOrderIds(assignments, { operationKind = "optimization" } = {}) {
  const retainOutgoingPm = operationKind !== "planning_reset";
  const orderIds = new Set();
  const collect = (item) => {
    [item?.order_id, item?.orderId,
      ...(Array.isArray(item?.order_ids) ? item.order_ids : []),
      ...(Array.isArray(item?.orderIds) ? item.orderIds : []),
    ].map(normalizePreviewText).filter(Boolean).forEach((orderId) => orderIds.add(orderId));
  };
  const collectFlaggedItems = (items) => {
    (Array.isArray(items) ? items : []).forEach((item) => {
      if (item?.is_end_of_day_load_source_preload === true || item?.isEndOfDayLoadSourcePreload === true
        || (retainOutgoingPm && (item?.is_pm_operational_loading === true || item?.isPmOperationalLoading === true))) {
        collect(item);
      }
    });
  };

  (Array.isArray(assignments) ? assignments : []).forEach((assignment) => {
    // Incoming PM links always stay on their truck. Optimization also keeps
    // outgoing PM loads; Reset may remove them and ordinary previous-day preloads.
    const markers = [
      ...(Array.isArray(assignment?.end_of_day_load_source_preloads) ? assignment.end_of_day_load_source_preloads : []),
      ...(retainOutgoingPm && Array.isArray(assignment?.end_of_day_loads) ? assignment.end_of_day_loads : []),
    ];
    markers.forEach((marker) => {
      collect(marker);
      (Array.isArray(marker?.items) ? marker.items : []).forEach(collect);
    });
    [assignment, ...(Array.isArray(assignment?.trips) ? assignment.trips : [])].forEach((container) => {
      [container?.orders, container?.delivery_items, container?.clients, container?.operational_steps,
        container?.stops, container?.routable_stops].forEach(collectFlaggedItems);
    });
  });
  return Array.from(orderIds);
}

export function collectPlanningResetRetainedOrderIds(assignments) {
  return collectOptimizationPreviewRetainedOrderIds(assignments, { operationKind: "planning_reset" });
}

export function filterOptimizationPreviewRetainedGroups(groups, retainedOrderIds = []) {
  const retainedIds = new Set(retainedOrderIds);
  return groups.filter((group) => !(
    Array.isArray(group?.orderIds) ? group.orderIds : Array.isArray(group?.order_ids) ? group.order_ids : []
  ).some((orderId) => retainedIds.has(normalizePreviewText(orderId))));
}

function readPreviewCount(candidate, ...keys) {
  for (const key of keys) {
    const rawValue = candidate?.[key];
    if (rawValue === null || rawValue === undefined || rawValue === "") {
      continue;
    }
    const value = Number(rawValue);
    if (Number.isFinite(value) && value >= 0) {
      return Math.floor(value);
    }
  }

  return null;
}

function readPreviewTimelineBounds(preview) {
  const groups = Array.isArray(preview?.groups) ? preview.groups : [];
  const starts = [];
  const ends = [];

  groups.forEach((group) => {
    if (group?.layoutKind !== "timeline") {
      return;
    }

    const startMinutes = Number(group?.startMinutes);
    const endMinutes = Number(group?.endMinutes);
    if (Number.isFinite(startMinutes)) {
      starts.push(startMinutes);
    }
    if (Number.isFinite(endMinutes)) {
      ends.push(endMinutes);
    }
  });

  if (!starts.length && !ends.length) {
    return null;
  }

  const startMinutes = starts.length ? Math.min(...starts) : Math.min(...ends);
  const endMinutes = ends.length ? Math.max(...ends) : startMinutes;

  return {
    endMinutes: Math.max(startMinutes, endMinutes),
    startMinutes,
  };
}

export function collectBoundedOptimizationPreviewOrderIds(groups, assignedOrderCount) {
  const limit = readPreviewCount({ assignedOrderCount }, "assignedOrderCount");
  if (!Array.isArray(groups) || limit === null || limit <= 0) {
    return [];
  }

  const revealedOrderIds = [];
  const revealedOrderIdSet = new Set();
  groups.forEach((group) => {
    if (revealedOrderIdSet.size >= limit) {
      return;
    }
    const unseenGroupOrderIds = Array.from(new Set(
      (Array.isArray(group?.orderIds)
        ? group.orderIds
        : Array.isArray(group?.order_ids) ? group.order_ids : [])
        .map(normalizePreviewText)
        .filter((orderId) => orderId && !revealedOrderIdSet.has(orderId)),
    ));
    if (!unseenGroupOrderIds.length
      || revealedOrderIdSet.size + unseenGroupOrderIds.length > limit) {
      return;
    }
    unseenGroupOrderIds.forEach((orderId) => {
      revealedOrderIdSet.add(orderId);
      revealedOrderIds.push(orderId);
    });
  });

  return revealedOrderIds;
}

export function mergeOptimizationPreviewAssignments(assignments, previewByTruckId, { active = false } = {}) {
  const sourceAssignments = Array.isArray(assignments) ? assignments.filter(Boolean) : [];
  if (!active || !(previewByTruckId instanceof Map) || previewByTruckId.size === 0) {
    return sourceAssignments;
  }

  const existingTruckIds = new Set(
    sourceAssignments
      .map((assignment) => normalizePreviewText(assignment?.truck_id || assignment?.truckId))
      .filter(Boolean),
  );
  const previewAssignments = [];

  previewByTruckId.forEach((preview, candidateTruckId) => {
    const truckId = normalizePreviewText(preview?.truckId || candidateTruckId);
    if (!truckId || existingTruckIds.has(truckId)) {
      return;
    }

    existingTruckIds.add(truckId);
    previewAssignments.push({
      delivery_items: [],
      operational_steps: [],
      orders: [],
      optimization_preview_placeholder: true,
      trips: [],
      truck_id: truckId,
      truck_name: truckId,
    });
  });

  return previewAssignments.length
    ? [...sourceAssignments, ...previewAssignments]
    : sourceAssignments;
}

export function mergeOptimizationPreviewTimelineBounds(rows, previewByTruckId, { active = false } = {}) {
  const sourceRows = Array.isArray(rows) ? rows : [];
  if (!active || !(previewByTruckId instanceof Map) || previewByTruckId.size === 0) {
    return sourceRows;
  }

  return sourceRows.map((row) => {
    const truckId = normalizePreviewText(row?.assignmentId || row?.truckId || row?.assignment?.truck_id);
    const previewBounds = readPreviewTimelineBounds(previewByTruckId.get(truckId));
    if (!previewBounds) {
      return row;
    }

    const rawDisplayedStartMinutes = row?.displayedTimelineStartMinutes;
    const rawDisplayedEndMinutes = row?.displayedTimelineEndMinutes;
    const displayedStartMinutes = rawDisplayedStartMinutes == null || rawDisplayedStartMinutes === ""
      ? null
      : Number(rawDisplayedStartMinutes);
    const displayedEndMinutes = rawDisplayedEndMinutes == null || rawDisplayedEndMinutes === ""
      ? null
      : Number(rawDisplayedEndMinutes);

    return {
      ...row,
      displayedTimelineEndMinutes: Number.isFinite(displayedEndMinutes)
        ? Math.max(displayedEndMinutes, previewBounds.endMinutes)
        : previewBounds.endMinutes,
      displayedTimelineStartMinutes: Number.isFinite(displayedStartMinutes)
        ? Math.min(displayedStartMinutes, previewBounds.startMinutes)
        : previewBounds.startMinutes,
    };
  });
}

export function timelineRowHasOptimizationPreview(row, previewByTruckId, { active = false } = {}) {
  if (!active || !(previewByTruckId instanceof Map)) {
    return false;
  }

  const truckId = normalizePreviewText(row?.assignmentId || row?.truckId || row?.assignment?.truck_id);
  return Boolean(truckId && previewByTruckId.has(truckId));
}

export function applyOptimizationPreviewReserveFallback(
  reserve,
  assignmentPreview,
  revealedOrderIds = [],
  fallbackTotalOrderCount = null,
) {
  const sourceReserve = reserve && typeof reserve === "object" ? reserve : {};
  const knownInitialCount = readPreviewCount(
    sourceReserve,
    "initialUnprotectedCount",
    "initialCount",
  );
  if (knownInitialCount !== null && (knownInitialCount > 0 || sourceReserve.stationaryCount > 0)) {
    return sourceReserve;
  }

  const previewTotalOrderCount = readPreviewCount(
    assignmentPreview,
    "totalOrderCount",
    "total_order_count",
  );
  const knownFallbackTotalOrderCount = readPreviewCount(
    { fallbackTotalOrderCount },
    "fallbackTotalOrderCount",
  );
  const totalOrderCount = previewTotalOrderCount !== null && previewTotalOrderCount > 0
    ? previewTotalOrderCount
    : (knownFallbackTotalOrderCount ?? previewTotalOrderCount);
  if (totalOrderCount === null) {
    return sourceReserve;
  }

  const normalizedRevealedOrderIds = Array.from(new Set(
    (Array.isArray(revealedOrderIds) ? revealedOrderIds : [])
      .map(normalizePreviewText)
      .filter(Boolean),
  ));

  return {
    ...sourceReserve,
    initialCount: totalOrderCount,
    initialUnprotectedCount: totalOrderCount,
    revealedCount: Math.min(normalizedRevealedOrderIds.length, totalOrderCount),
    revealedOrderIds: normalizedRevealedOrderIds,
  };
}
